import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Plus, Pencil, Trash2, ChevronDown, ChevronRight,
  Variable, ToggleLeft, Eye, X, GripVertical,
} from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import {
  api,
  type AgreementTemplate,
  type AgreementTemplateInput,
  type TemplateVariable,
  type OptionalBlock,
} from "@/lib/api";
import { cn } from "@/lib/utils";

// ─── Variable row ─────────────────────────────────────────────────────────────

function VariableRow({
  variable,
  onChange,
  onRemove,
}: {
  variable: TemplateVariable;
  onChange: (v: TemplateVariable) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <GripVertical className="w-4 h-4 text-muted-foreground/40 shrink-0" />
      <Input
        value={variable.label}
        onChange={(e) => onChange({ ...variable, label: e.target.value })}
        placeholder="Label (e.g. Client Name)"
        className="h-7 text-xs flex-1"
      />
      <Input
        value={variable.name}
        onChange={(e) => onChange({ ...variable, name: e.target.value.replace(/\s+/g, "_").toLowerCase() })}
        placeholder="Key (e.g. client_name)"
        className="h-7 text-xs flex-1 font-mono"
      />
      <Input
        value={variable.default_value ?? ""}
        onChange={(e) => onChange({ ...variable, default_value: e.target.value })}
        placeholder="Default"
        className="h-7 text-xs w-28"
      />
      <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600" onClick={onRemove}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// ─── Optional block row ───────────────────────────────────────────────────────

function OptionalBlockRow({
  block,
  onChange,
  onRemove,
}: {
  block: OptionalBlock;
  onChange: (b: OptionalBlock) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="border rounded-md p-3 space-y-2 bg-muted/20">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setExpanded(!expanded)} className="text-muted-foreground">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Input
          value={block.label}
          onChange={(e) => onChange({ ...block, label: e.target.value })}
          placeholder="Block label (e.g. Services Section)"
          className="h-7 text-xs flex-1"
        />
        <Input
          value={block.name}
          onChange={(e) => onChange({ ...block, name: e.target.value.replace(/\s+/g, "_").toLowerCase() })}
          placeholder="Key (e.g. services_section)"
          className="h-7 text-xs w-40 font-mono"
        />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={block.default_enabled}
            onChange={(e) => onChange({ ...block, default_enabled: e.target.checked })}
            className="rounded"
          />
          On by default
        </label>
        <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600" onClick={onRemove}>
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
      {expanded && (
        <Textarea
          value={block.content}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          placeholder="Block content — use {{variable_name}} placeholders. In the document body, reference this block with {{#include:block_key}}"
          className="text-xs min-h-[80px] resize-y font-mono"
          rows={3}
        />
      )}
    </div>
  );
}

// ─── Template editor ──────────────────────────────────────────────────────────

const EMPTY_TEMPLATE: AgreementTemplateInput = {
  name: "",
  description: "",
  body: "",
  variables: [],
  optional_blocks: [],
};

function TemplateEditor({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: AgreementTemplateInput;
  onSave: (data: AgreementTemplateInput) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<AgreementTemplateInput>(initial);
  const [showPreview, setShowPreview] = useState(false);

  const setField = <K extends keyof AgreementTemplateInput>(k: K, v: AgreementTemplateInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const addVariable = () =>
    setField("variables", [...form.variables, { name: "", label: "", default_value: "" }]);

  const addBlock = () =>
    setField("optional_blocks", [
      ...form.optional_blocks,
      { name: "", label: "", content: "", default_enabled: true },
    ]);

  // Preview: replace {{vars}} with defaults or key name
  const previewBody = showPreview
    ? form.body
        .replace(/\{\{([^}]+)\}\}/g, (_, name) => {
          const v = form.variables.find((vv) => vv.name === name.trim());
          return v?.default_value || `[${name.trim()}]`;
        })
        .replace(/\{\{#include:([^}]+)\}\}/g, (_, name) => {
          const b = form.optional_blocks.find((bb) => bb.name === name.trim());
          return b ? `\n\n--- ${b.label} ---\n${b.content}` : "";
        })
    : null;

  return (
    <div className="space-y-5">
      {/* Name + description */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Template Name *</Label>
          <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Fixed Price Agreement" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Description</Label>
          <Input value={form.description ?? ""} onChange={(e) => setField("description", e.target.value)} placeholder="Brief description" />
        </div>
      </div>

      {/* Variables */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs flex items-center gap-1.5">
            <Variable className="w-3.5 h-3.5" /> Variables
          </Label>
          <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" onClick={addVariable}>
            <Plus className="w-3 h-3 mr-1" /> Add variable
          </Button>
        </div>
        {form.variables.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No variables yet. Add variables to use <code className="bg-muted px-1 rounded">{"{{variable_name}}"}</code> in your template body.</p>
        )}
        <div className="space-y-1.5">
          {form.variables.map((v, i) => (
            <VariableRow
              key={i}
              variable={v}
              onChange={(nv) => setField("variables", form.variables.map((x, j) => j === i ? nv : x))}
              onRemove={() => setField("variables", form.variables.filter((_, j) => j !== i))}
            />
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Document Body</Label>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <Eye className="w-3.5 h-3.5" /> {showPreview ? "Edit" : "Preview"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Use <code className="bg-muted px-1 rounded">{"{{variable_name}}"}</code> for variables.
          Start a line with <code className="bg-muted px-1 rounded"># Heading</code> for section headings.
          Separate paragraphs with a blank line.
          Reference optional blocks inline with <code className="bg-muted px-1 rounded">{"{{#include:block_key}}"}</code>.
        </p>
        {showPreview ? (
          <div className="border rounded-md p-4 min-h-[300px] text-sm whitespace-pre-wrap bg-muted/20 font-mono text-xs">
            {previewBody || <span className="text-muted-foreground italic">Empty</span>}
          </div>
        ) : (
          <Textarea
            value={form.body}
            onChange={(e) => setField("body", e.target.value)}
            placeholder={"Dear {{client_name}},\n\nThis agreement confirms our engagement...\n\n# Fee Summary\n\nTotal fees: {{fee_amount}}\n\n{{#include:additional_terms}}"}
            className="min-h-[300px] resize-y font-mono text-xs"
          />
        )}
      </div>

      {/* Optional blocks */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs flex items-center gap-1.5">
            <ToggleLeft className="w-3.5 h-3.5" /> Optional Blocks
          </Label>
          <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" onClick={addBlock}>
            <Plus className="w-3 h-3 mr-1" /> Add block
          </Button>
        </div>
        <p className="text-xs text-muted-foreground italic">
          Optional blocks can be toggled on/off when sending. Reference them inline with <code className="bg-muted px-1 rounded">{"{{#include:block_key}}"}</code> or leave them out to append at the end.
        </p>
        <div className="space-y-2">
          {form.optional_blocks.map((b, i) => (
            <OptionalBlockRow
              key={i}
              block={b}
              onChange={(nb) => setField("optional_blocks", form.optional_blocks.map((x, j) => j === i ? nb : x))}
              onRemove={() => setField("optional_blocks", form.optional_blocks.filter((_, j) => j !== i))}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="button" onClick={() => onSave(form)} disabled={saving || !form.name.trim()}>
          {saving ? "Saving…" : "Save Template"}
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AgreementTemplates() {
  const [editing, setEditing] = useState<AgreementTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const qc = useQueryClient();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["agreement-templates"],
    queryFn: () => api.getAgreementTemplates(),
  });

  const createMutation = useMutation({
    mutationFn: (data: AgreementTemplateInput) => api.createAgreementTemplate(data),
    onSuccess: () => {
      toast({ title: "Template created" });
      qc.invalidateQueries({ queryKey: ["agreement-templates"] });
      setCreating(false);
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AgreementTemplateInput> }) =>
      api.updateAgreementTemplate(id, data),
    onSuccess: () => {
      toast({ title: "Template updated" });
      qc.invalidateQueries({ queryKey: ["agreement-templates"] });
      setEditing(null);
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAgreementTemplate(id),
    onSuccess: () => {
      toast({ title: "Template deleted" });
      qc.invalidateQueries({ queryKey: ["agreement-templates"] });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  return (
    <Layout>
      <div className="p-6 space-y-5 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-violet-600" />
            <h1 className="text-xl font-semibold">Agreement Templates</h1>
          </div>
          {!creating && !editing && (
            <Button onClick={() => setCreating(true)} className="gap-1.5">
              <Plus className="w-4 h-4" /> New Template
            </Button>
          )}
        </div>

        {/* Create form */}
        {creating && (
          <div className="border rounded-lg p-5 bg-card space-y-1">
            <h2 className="font-medium text-sm mb-4">New Template</h2>
            <TemplateEditor
              initial={EMPTY_TEMPLATE}
              onSave={(data) => createMutation.mutate(data)}
              onCancel={() => setCreating(false)}
              saving={createMutation.isPending}
            />
          </div>
        )}

        {/* Edit form */}
        {editing && (
          <div className="border rounded-lg p-5 bg-card">
            <h2 className="font-medium text-sm mb-4">Edit: {editing.name}</h2>
            <TemplateEditor
              initial={editing}
              onSave={(data) => updateMutation.mutate({ id: editing.id, data })}
              onCancel={() => setEditing(null)}
              saving={updateMutation.isPending}
            />
          </div>
        )}

        {/* Template list */}
        {!creating && !editing && (
          <div className="space-y-2">
            {isLoading && <p className="text-sm text-muted-foreground p-4">Loading…</p>}
            {!isLoading && templates.length === 0 && (
              <div className="border rounded-lg p-12 text-center space-y-2">
                <FileText className="w-10 h-10 text-muted-foreground/40 mx-auto" />
                <p className="text-sm text-muted-foreground">No templates yet</p>
                <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
                  Create your first template
                </Button>
              </div>
            )}
            {templates.map((t: AgreementTemplate) => (
              <div key={t.id} className="border rounded-lg p-4 bg-card flex items-start justify-between gap-4 hover:bg-muted/20 transition-colors">
                <div className="space-y-1 min-w-0">
                  <div className="font-medium text-sm">{t.name}</div>
                  {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {t.variables.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                        <Variable className="w-3 h-3" /> {t.variables.length} variable{t.variables.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    {t.optional_blocks.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full">
                        <ToggleLeft className="w-3 h-3" /> {t.optional_blocks.length} optional block{t.optional_blocks.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(t)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-rose-600 hover:text-rose-700"
                    onClick={async () => {
                      if (await askConfirm({
                        title: "Delete this agreement template?",
                        subject: "agreement template",
                        name: t.name,
                        body: "will be deleted.",
                        confirmLabel: "Delete",
                        destructive: true,
                      })) deleteMutation.mutate(t.id);
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
